import { gtm } from './analytics';
import { trackingService } from '../services/trackingService';

// Throttle utility function
function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

// Scroll Depth Tracking
let scrollDepthFired = {
    25: false,
    50: false,
    75: false,
    100: false,
};

function handleScrollDepth() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = Math.round((scrollTop / docHeight) * 100);

    [25, 50, 75, 100].forEach((threshold) => {
        if (scrolled >= threshold && !scrollDepthFired[threshold]) {
            scrollDepthFired[threshold] = true;

            // Send to GTM
            gtm.pushEvent('scroll_depth', {
                percentage: threshold,
                scroll_depth: threshold
            });

            // Send to your backend (optional)
            try {
                trackingService.sendInteraction({
                    type: 'scroll_depth',
                    sessionId: window.platypusSessionId || 'unknown',
                    page: window.location.pathname,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                    scrollPercentage: threshold,
                    scrollPosition: scrollTop
                });
            } catch (error) {
                console.warn('Failed to send scroll depth to backend:', error);
            }
        }
    });
}

export function initScrollDepthTracking() {
    const throttledHandler = throttle(handleScrollDepth, 500);
    window.addEventListener('scroll', throttledHandler);

    return () => {
        window.removeEventListener('scroll', throttledHandler);
    };
}

// Section Visibility Tracking
const SECTIONS = [
    { id: 'home', event: 'section_visible_hero' },
    { id: 'whyinfinity', event: 'section_visible_why_infinity' },
    { id: 'aboutus', event: 'section_visible_about' },
    { id: 'problem-solution', event: 'section_visible_problem_solution' },
    { id: 'contactus', event: 'section_visible_contact' },
];

const sectionTriggered = {};

export function initSectionVisibilityTracking() {
    if (typeof window === 'undefined') return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach(entry => {
                const id = entry.target.id;
                const config = SECTIONS.find(s => s.id === id);

                if (entry.isIntersecting && config && !sectionTriggered[config.event]) {
                    sectionTriggered[config.event] = true;

                    // Send to GTM
                    gtm.pushEvent(config.event, {
                        section_id: id,
                        visibility_percentage: Math.round(entry.intersectionRatio * 100)
                    });

                    // Send to your backend (optional)
                    try {
                        trackingService.sendInteraction({
                            type: 'section_view',
                            sessionId: window.platypusSessionId || 'unknown',
                            page: window.location.pathname,
                            userAgent: navigator.userAgent,
                            timestamp: new Date().toISOString(),
                            sectionId: id,
                            sectionEvent: config.event,
                            visibilityRatio: entry.intersectionRatio
                        });
                    } catch (error) {
                        console.warn('Failed to send section view to backend:', error);
                    }
                }
            });
        },
        {
            root: null,
            threshold: 0.5, // 50% of section must be visible
        }
    );

    // Observe sections with a delay to ensure DOM is ready
    setTimeout(() => {
        SECTIONS.forEach(section => {
            const element = document.getElementById(section.id);
            if (element) {
                observer.observe(element);
            } else {
                console.warn(`Section element not found: ${section.id}`);
            }
        });
    }, 1000);

    return () => {
        observer.disconnect();
    };
}

// Time on Page Tracking
let timeTrackingFired = {
    10: false,
    30: false,
    60: false,
    120: false,
};

export function initTimeOnPageTracking() {
    if (typeof window === 'undefined') return;

    const timePoints = [
        { seconds: 10, label: '10s' },
        { seconds: 30, label: '30s' },
        { seconds: 60, label: '1m' },
        { seconds: 120, label: '2m' },
    ];

    timePoints.forEach(({ seconds, label }) => {
        setTimeout(() => {
            if (!timeTrackingFired[seconds]) {
                timeTrackingFired[seconds] = true;

                // Send to GTM
                gtm.pushEvent(`time_on_page_${label}`, {
                    time_on_page_seconds: seconds,
                    page_location: window.location.pathname
                });

                // Send to your backend (optional)
                try {
                    trackingService.sendInteraction({
                        type: 'time_milestone',
                        sessionId: window.platypusSessionId || 'unknown',
                        page: window.location.pathname,
                        userAgent: navigator.userAgent,
                        timestamp: new Date().toISOString(),
                        timeOnPageSeconds: seconds,
                        milestone: label
                    });
                } catch (error) {
                    console.warn('Failed to send time milestone to backend:', error);
                }
            }
        }, seconds * 1000);
    });
}

// Enhanced Form Tracking
export function trackFormInteraction(formName, interactionType, formData = {}) {
    // Send to GTM
    gtm.trackFormSubmission(formName, {
        interaction_type: interactionType,
        ...formData
    });

    // Send to your backend
    try {
        trackingService.sendInteraction({
            type: 'form_interaction',
            sessionId: window.platypusSessionId || 'unknown',
            page: window.location.pathname,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            formName,
            interactionType,
            formData
        });
    } catch (error) {
        console.warn('Failed to send form interaction to backend:', error);
    }
}

// Enhanced Button Click Tracking
export function trackButtonClick(buttonName, buttonContext = {}) {
    // Send to GTM
    gtm.trackButtonClick(buttonName, window.location.pathname);

    // Send to your backend
    try {
        trackingService.sendInteraction({
            type: 'button_click',
            sessionId: window.platypusSessionId || 'unknown',
            page: window.location.pathname,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            buttonName,
            ...buttonContext
        });
    } catch (error) {
        console.warn('Failed to send button click to backend:', error);
    }
}

// Initialize all tracking
export function initAllTracking() {
    const cleanupFunctions = [];

    // Set a global session ID for consistency
    if (!window.platypusSessionId) {
        window.platypusSessionId = crypto.randomUUID();
    }

    try {
        cleanupFunctions.push(initScrollDepthTracking());
        cleanupFunctions.push(initSectionVisibilityTracking());
        initTimeOnPageTracking();

        console.log('✅ All tracking initialized successfully');
    } catch (error) {
        console.error('❌ Error initializing tracking:', error);
    }

    // Return cleanup function
    return () => {
        cleanupFunctions.forEach(cleanup => {
            if (typeof cleanup === 'function') {
                cleanup();
            }
        });
    };
}
